import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { Rarity, CurrentEvent } from '../types/api'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format numbers with dots every 3 digits (e.g., 1.000.000)
export function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

export function formatDate(date: number): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatTimeLeft(endTimeUnix: number, currentTimeUnix?: number): string {
  const now = currentTimeUnix || Math.floor(Date.now() / 1000)
  const timeLeft = endTimeUnix - now
  
  if (timeLeft <= 0) return 'Expired'
  
  const days = Math.floor(timeLeft / 86400)
  const hours = Math.floor((timeLeft % 86400) / 3600)
  const minutes = Math.floor((timeLeft % 3600) / 60)
  const seconds = timeLeft % 60
  
  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  } else {
    return `${seconds}s`
  }
}

export function getStockStatus(quantity: number): 'in-stock' | 'low-stock' | 'out-of-stock' {
  if (quantity === 0) return 'out-of-stock'
  if (quantity <= 10) return 'low-stock'
  return 'in-stock'
}

export function getStockStatusColor(status: 'in-stock' | 'low-stock' | 'out-of-stock'): string {
  switch (status) {
    case 'in-stock':
      return 'text-foreground'
    case 'low-stock':
      return 'text-foreground'
    case 'out-of-stock':
      return 'text-muted-foreground'
    default:
      return 'text-muted-foreground'
  }
}

export function getRarityColor(rarity: string): string {
  switch (rarity?.toLowerCase()) {
    case 'common':
      return 'text-gray-600'
    case 'uncommon':
      return 'text-green-600'
    case 'rare':
      return 'text-blue-600'
    case 'epic':
      return 'text-purple-600'
    case 'legendary':
      return 'text-orange-600'
    case 'mythic':
      return 'text-red-600'
    case 'divine':
      return 'text-yellow-400'
    case 'mythical':
      return 'text-cyan-400'
    case 'prismatic':
      return 'text-cyan-200 font-semibold'
    default:
      return 'text-muted-foreground'
  }
}

export function getRarityBadgeColor(rarity: string): string {
  switch (rarity?.toLowerCase()) {
    case 'common':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
    case 'uncommon':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'rare':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    case 'epic':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    case 'legendary':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    case 'mythic':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    case 'divine':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    case 'mythical':
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
    case 'prismatic':
      return 'bg-white/20 text-white dark:bg-white/10 dark:text-white'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

export function formatRarity(rarity: string): string {
  if (!rarity) return 'Unknown'
  return rarity.charAt(0).toUpperCase() + rarity.slice(1).toLowerCase()
}

// Game-accurate rarity sorting function
export function getRaritySortValue(rarity: string): number {
  switch (rarity?.toLowerCase()) {
    case 'prismatic':
      return 0
    case 'mythical':
      return 1
    case 'divine':
      return 2
    case 'mythic':
      return 3
    case 'legendary':
      return 4
    case 'epic':
      return 5
    case 'rare':
      return 6
    case 'uncommon':
      return 7
    case 'common':
      return 8
    default:
      return 9 // unknown/undefined rarities go last
  }
}

// Function to sort items by rarity (highest to lowest)
export function sortByRarity<T extends { item_id: string }>(
  items: T[], 
  rarityMap: Map<string, string>
): T[] {
  return [...items].sort((a, b) => {
    const rarityA = rarityMap.get(a.item_id) || 'unknown'
    const rarityB = rarityMap.get(b.item_id) || 'unknown'
    return getRaritySortValue(rarityA) - getRaritySortValue(rarityB)
  })
}

// Function to get shine effect class based on rarity
export function getRarityShineClass(rarity: string): string {
  switch (rarity?.toLowerCase()) {
    case 'prismatic':
      return 'shine-prismatic glow-prismatic sparkle-prismatic'
    case 'mythical':
      return 'shine-mythical glow-mythical sparkle-mythical'
    case 'divine':
      return 'shine-divine glow-divine sparkle-divine'
    case 'mythic':
      return 'shine-mythic glow-mythic sparkle-mythic'
    case 'legendary':
      return 'shine-legendary glow-legendary'
    case 'epic':
      return 'shine-epic glow-epic'
    case 'rare':
      return 'shine-rare'
    case 'uncommon':
      return 'shine-uncommon'
    case 'common':
      return 'shine-common'
    default:
      return ''
  }
} 

// Traveling Merchant Timing Utilities
export function calculateMerchantSchedule(currentTime: number = Date.now() / 1000) {
  // Merchant appears every 4-6 hours, stays for 2-3 hours
  // This is a simplified calculation - adjust based on actual game mechanics
  
  const now = new Date(currentTime * 1000)
  const hour = now.getHours()
  const minute = now.getMinutes()
  
  // Calculate next merchant arrival (every 4-6 hours from game start)
  // Assuming merchant cycles: 0:00, 4:00, 8:00, 12:00, 16:00, 20:00
  const merchantHours = [0, 4, 8, 12, 16, 20]
  let nextArrival = null
  let currentArrival = null
  
  for (let i = 0; i < merchantHours.length; i++) {
    const merchantHour = merchantHours[i]
    const arrivalTime = new Date(now)
    arrivalTime.setHours(merchantHour, 0, 0, 0)
    
    if (arrivalTime.getTime() / 1000 > currentTime) {
      nextArrival = arrivalTime.getTime() / 1000
      break
    }
    
    // Check if merchant is currently active
    const departureTime = new Date(arrivalTime)
    departureTime.setHours(merchantHour + 3, 0, 0, 0) // Merchant stays for 3 hours
    
    if (currentTime >= arrivalTime.getTime() / 1000 && currentTime < departureTime.getTime() / 1000) {
      currentArrival = arrivalTime.getTime() / 1000
    }
  }
  
  // If no next arrival found today, calculate for tomorrow
  if (!nextArrival) {
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(merchantHours[0], 0, 0, 0)
    nextArrival = tomorrow.getTime() / 1000
  }
  
  return {
    currentArrival,
    nextArrival,
    isActive: currentArrival !== null,
    timeUntilNext: nextArrival ? nextArrival - currentTime : 0
  }
}

export function formatMerchantTime(time: number): string {
  const date = new Date(time * 1000)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

export function getMerchantStatusText(schedule: ReturnType<typeof calculateMerchantSchedule>): string {
  if (schedule.isActive) {
    return 'Currently Active'
  }
  
  const hours = Math.floor(schedule.timeUntilNext / 3600)
  const minutes = Math.floor((schedule.timeUntilNext % 3600) / 60)
  
  if (hours > 0) {
    return `Arrives in ${hours}h ${minutes}m`
  } else {
    return `Arrives in ${minutes}m`
  }
}

export function getMerchantStatusColor(schedule: ReturnType<typeof calculateMerchantSchedule>): string {
  if (schedule.isActive) {
    return 'text-green-400'
  }
  
  if (schedule.timeUntilNext < 1800) { // Less than 30 minutes
    return 'text-yellow-400'
  }
  
  return 'text-muted-foreground'
} 

// Mutation Weather Synchronization Utilities
interface MutationPeak {
  time: number
  weatherType: string
  chance: number
  description: string
  endTime?: number
}

export function calculateMutationWeatherSchedule(currentTime: number = Date.now() / 1000) {
  // Mutation weather patterns based on game mechanics
  // Different weather types have different mutation boost cycles
  
  const now = new Date(currentTime * 1000)
  const hour = now.getHours()
  const minute = now.getMinutes()
  
  // Mutation weather cycles (when they're most effective)
  const mutationWeatherCycles = {
    stormy: {
      peakHours: [2, 6, 10, 14, 18, 22], // Every 4 hours
      duration: 2, // 2 hours peak effectiveness
      baseChance: 0.15,
      description: "Stormy weather peaks every 4 hours"
    },
    rainy: {
      peakHours: [1, 5, 9, 13, 17, 21], // Every 4 hours, offset
      duration: 3, // 3 hours peak effectiveness
      baseChance: 0.10,
      description: "Rainy weather peaks every 4 hours"
    },
    snowy: {
      peakHours: [0, 4, 8, 12, 16, 20], // Every 4 hours
      duration: 4, // 4 hours peak effectiveness
      baseChance: 0.12,
      description: "Snowy weather peaks every 4 hours"
    },
    windy: {
      peakHours: [3, 7, 11, 15, 19, 23], // Every 4 hours, offset
      duration: 2, // 2 hours peak effectiveness
      baseChance: 0.08,
      description: "Windy weather peaks every 4 hours"
    }
  }
  
  let nextMutationPeak: MutationPeak | null = null
  let currentMutationPeak: MutationPeak | null = null
  let bestWeatherType: string | null = null
  let maxChance = 0.02 // Base mutation chance
  
  // Check each weather type for current and next peaks
  Object.entries(mutationWeatherCycles).forEach(([weatherType, cycle]) => {
    for (let i = 0; i < cycle.peakHours.length; i++) {
      const peakHour = cycle.peakHours[i]
      const peakStart = new Date(now)
      peakStart.setHours(peakHour, 0, 0, 0)
      
      if (peakStart.getTime() / 1000 > currentTime) {
        // This is a future peak
        if (!nextMutationPeak || peakStart.getTime() / 1000 < nextMutationPeak.time) {
          nextMutationPeak = {
            time: peakStart.getTime() / 1000,
            weatherType,
            chance: cycle.baseChance,
            description: cycle.description
          }
        }
        break
      }
      
      // Check if currently in peak period
      const peakEnd = new Date(peakStart)
      peakEnd.setHours(peakHour + cycle.duration, 0, 0, 0)
      
      if (currentTime >= peakStart.getTime() / 1000 && currentTime < peakEnd.getTime() / 1000) {
        currentMutationPeak = {
          time: peakStart.getTime() / 1000,
          weatherType,
          chance: cycle.baseChance,
          description: cycle.description,
          endTime: peakEnd.getTime() / 1000
        }
        
        if (cycle.baseChance > maxChance) {
          maxChance = cycle.baseChance
          bestWeatherType = weatherType
        }
      }
    }
  })
  
  // If no next peak found today, calculate for tomorrow
  if (!nextMutationPeak) {
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(mutationWeatherCycles.stormy.peakHours[0], 0, 0, 0)
    nextMutationPeak = {
      time: tomorrow.getTime() / 1000,
      weatherType: 'stormy',
      chance: mutationWeatherCycles.stormy.baseChance,
      description: mutationWeatherCycles.stormy.description
    }
  }
  
  return {
    currentPeak: currentMutationPeak,
    nextPeak: nextMutationPeak,
    isActive: currentMutationPeak !== null,
    bestWeatherType,
    maxChance: maxChance + 0.02, // Add base chance
    timeUntilNext: nextMutationPeak ? nextMutationPeak.time - currentTime : 0
  } as {
    currentPeak: MutationPeak | null
    nextPeak: MutationPeak | null
    isActive: boolean
    bestWeatherType: string | null
    maxChance: number
    timeUntilNext: number
  }
}

export function getMutationWeatherStatusText(schedule: ReturnType<typeof calculateMutationWeatherSchedule>): string {
  if (schedule.isActive && schedule.currentPeak) {
    const timeLeft = (schedule.currentPeak.endTime || 0) - Math.floor(Date.now() / 1000)
    const minutes = Math.floor(timeLeft / 60)
    
    if (minutes > 0) {
      return `${schedule.currentPeak.weatherType} mutation peak - ${minutes}m left`
    } else {
      return `${schedule.currentPeak.weatherType} mutation peak ending soon`
    }
  }
  
  const hours = Math.floor(schedule.timeUntilNext / 3600)
  const minutes = Math.floor((schedule.timeUntilNext % 3600) / 60)
  
  if (hours > 0) {
    return `Next ${schedule.nextPeak?.weatherType || 'mutation'} peak in ${hours}h ${minutes}m`
  } else {
    return `Next ${schedule.nextPeak?.weatherType || 'mutation'} peak in ${minutes}m`
  }
}

export function getMutationWeatherStatusColor(schedule: ReturnType<typeof calculateMutationWeatherSchedule>): string {
  if (schedule.isActive) {
    return 'text-green-400'
  }
  
  if (schedule.timeUntilNext < 1800) { // Less than 30 minutes
    return 'text-yellow-400'
  }
  
  return 'text-muted-foreground'
}

export function getMutationWeatherIcon(weatherType: string): string {
  const icons = {
    stormy: '⚡',
    rainy: '🌧️',
    snowy: '❄️',
    windy: '💨',
    sunny: '☀️',
    cloudy: '☁️'
  }
  return icons[weatherType as keyof typeof icons] || '🌱'
} 

// Event Timing Utilities
export function calculateEventSchedule(eventData: CurrentEvent, currentTime: number = Date.now() / 1000) {
  if (!eventData?.current) {
    return {
      isActive: false,
      currentEvent: null,
      nextEvent: null,
      timeUntilNext: 0
    }
  }

  const now = new Date(currentTime * 1000)
  const eventStart = new Date(now)
  eventStart.setHours(eventData.current.start.hour, eventData.current.start.minute, 0, 0)
  
  // If event start time has passed today, it's active
  const eventStartTime = eventStart.getTime() / 1000
  const eventEndTime = eventStartTime + (24 * 3600) // Assume 24-hour duration
  
  const isActive = currentTime >= eventStartTime && currentTime < eventEndTime
  const timeUntilStart = eventStartTime - currentTime
  const timeUntilEnd = eventEndTime - currentTime

  return {
    isActive,
    currentEvent: {
      name: eventData.current.name,
      startTime: eventStartTime,
      endTime: eventEndTime,
      icon: eventData.current.icon,
      description: eventData.current.description,
      rewards: eventData.current.rewards,
      requirements: eventData.current.requirements,
      isActive,
      timeUntilStart,
      timeUntilEnd
    },
    nextEvent: eventData.upcoming?.[0] || null,
    timeUntilNext: isActive ? 0 : Math.max(0, timeUntilStart)
  }
}

export function getEventStatusText(schedule: ReturnType<typeof calculateEventSchedule>): string {
  if (schedule.isActive && schedule.currentEvent) {
    const timeLeft = schedule.currentEvent.timeUntilEnd || 0
    const hours = Math.floor(timeLeft / 3600)
    const minutes = Math.floor((timeLeft % 3600) / 60)
    
    if (hours > 0) {
      return `Active - ${hours}h ${minutes}m remaining`
    } else {
      return `Active - ${minutes}m remaining`
    }
  }
  
  const hours = Math.floor(schedule.timeUntilNext / 3600)
  const minutes = Math.floor((schedule.timeUntilNext % 3600) / 60)
  
  if (hours > 0) {
    return `Starts in ${hours}h ${minutes}m`
  } else {
    return `Starts in ${minutes}m`
  }
}

export function getEventStatusColor(schedule: ReturnType<typeof calculateEventSchedule>): string {
  if (schedule.isActive) {
    return 'text-green-400'
  }
  
  if (schedule.timeUntilNext < 3600) { // Less than 1 hour
    return 'text-yellow-400'
  }
  
  return 'text-muted-foreground'
}

export function formatEventTime(time: number): string {
  const date = new Date(time * 1000)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
} 